import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Button, Flex, Text } from '@raystack/apsara';

import { ExploreFilters } from '@src/clients/seeker/api';
import { IdentityContext } from '@src/contexts/identity/context';
import { For } from '@src/hoc';
import useConstellations from '@src/hooks/useConstellation';
import {
  setCloudCoverage,
  setEndDate,
  setSatellite,
  setShowCloudCoverage,
  setStartDate
} from '@src/redux/_reducers/filtersReducer';
import { addSatellites } from '@src/utils/AoiUtils';
import { ImageSearchParams } from '@src/utils/constants/COPILOT_CONSTANTS';
import { SENTINEL_2_FILTER_DATA } from '@src/utils/constants/LENS_CONSTANTS';
import { getUSTimeFormat } from '@src/utils/dateUtils';

import { getCenterFromBBox, reverseGeocode } from './BBoxToLocation';

import styles from './ImagesSearch.module.css';

interface ImageSearchParamsUIProps {
  queryParameters: ExploreFilters | null;
}
interface Constellation {
  name: string; // Add other properties if needed
  key: string; // Optional: If there are unknown additional properties
}

const formatDateRange = (queryParameters: ExploreFilters): string | null => {
  const { start_time = '', end_time = '' } = queryParameters;

  if (!start_time || !end_time) {
    return null;
  }

  const startDate = getUSTimeFormat(`${start_time}`);
  const endDate = getUSTimeFormat(`${end_time}`);
  return `${startDate} - ${endDate}`;
};

const formatCloudCoverage = (
  queryParameters: ExploreFilters
): string | null => {
  const { minimum_cloud_cover, maximum_cloud_cover } = queryParameters;
  if (minimum_cloud_cover == null && maximum_cloud_cover == null) {
    return null;
  }
  return `${minimum_cloud_cover || 0} - ${maximum_cloud_cover || 100}%`;
};

const formatSatellites = (queryParameters: ExploreFilters): string | null => {
  const { satellites } = queryParameters;
  return Array.isArray(satellites) && satellites.length > 0
    ? satellites.join(', ')
    : satellites != null
      ? String(satellites)
      : null;
};

export const ImageSearchParamsUI: React.FC<ImageSearchParamsUIProps> = ({
  queryParameters
}) => {
  // Handle the case when queryParameters is null
  const dateRange = queryParameters ? formatDateRange(queryParameters) : null;
  const cloudCoverage = queryParameters
    ? formatCloudCoverage(queryParameters)
    : null;
  const satellites = queryParameters ? formatSatellites(queryParameters) : null;

  const [locationName, setLocationName] = useState<string | null>(null);
  const [loadingLocation, setLoadingLocation] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { activeOrg } = useContext(IdentityContext);

  useEffect(() => {
    if (queryParameters?.bbox && Array.isArray(queryParameters.bbox)) {
      const fetchLocationName = async () => {
        setLoadingLocation(true);
        setError(null);
        try {
          const bbox = queryParameters.bbox as number[];
          const center = getCenterFromBBox(bbox);
          const location = await reverseGeocode(center.lat, center.lng);
          setLocationName(location || 'Unknown Location');
        } catch (err) {
          setError('Failed to fetch location');
        } finally {
          setLoadingLocation(false);
        }
      };
      fetchLocationName();
    }
  }, [queryParameters?.bbox]);

  const renderParam = (label: string, value: string | null) => {
    if (!value || value === 'Invalid date - Invalid date') {
      return null;
    }
    if (value) {
      return (
        <Flex
          direction='row'
          align='center'
          gap='small'
          className={styles.cardContent}
        >
          <Text className={styles.keys}>{label}:</Text>
          <Text>{value}</Text>
        </Flex>
      );
    }
    return null;
  };

  const parameters = useMemo(
    () => [
      { label: ImageSearchParams.DateRange, value: dateRange },
      { label: ImageSearchParams.CloudCover, value: cloudCoverage },
      { label: ImageSearchParams.Satellites, value: satellites }
    ],
    [dateRange, cloudCoverage, satellites]
  );

  // Fetch constellation using SWR
  const { constellations, sentinel2Data, isLoading } = useConstellations();

  const dispatch = useDispatch();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const filters = useSelector(state => state.filters);

  const RedirectToExplore = useCallback(() => {
    // Redirect to the active organization page
    navigate(`/${activeOrg?.name}`, {
      state: {
        showNewFilter: true,
        bbox: queryParameters?.bbox
      }
    });

    // Delay the processing of parameters to ensure navigation has occurred
    setTimeout(() => {
      if (parameters?.length > 0) {
        parameters.forEach(param => {
          const { label, value } = param;
          switch (label) {
            case 'Date Range': {
              if (value) {
                const [startDate, endDate] = value
                  .split(' - ')
                  .map(date => new Date(date.trim())); // Trim whitespace before parsing

                // Check if both dates are valid before dispatching
                if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                  dispatch(setStartDate(startDate));
                  dispatch(setEndDate(endDate));
                }
              }
              break;
            }

            case 'Cloud Coverage': {
              if (value) {
                const [minCloud, maxCloud] = value
                  .replace('%', '') // Remove '%' symbol
                  .split('-')
                  .map(val => Number(val.trim())); // Convert to numbers after trimming
                dispatch(setShowCloudCoverage(true));
                dispatch(setCloudCoverage([minCloud, maxCloud]));
              } else {
                dispatch(setShowCloudCoverage(false));
              }
              break;
            }
            case 'Satellites': {
              if (value) {
                const satelliteNames = value
                  .split(',')
                  .map(name => name.trim());
                // Filter constellations based on satellite names
                const filteredConstellations = satelliteNames.length
                  ? constellations.filter(({ name }: Constellation) =>
                      satelliteNames.includes(name)
                    )
                  : [];
                // If there are any filtered constellations, add each one to the satellite array
                addSatellites(filteredConstellations, dispatch);
              } else {
                if (!isLoading) {
                  dispatch(
                    setSatellite([
                      {
                        ...SENTINEL_2_FILTER_DATA,
                        satellite_id: sentinel2Data.id
                      }
                    ])
                  );
                }
              }
              break;
            }

            default:
              break;
          }
        });
      }
    }, 50);
  }, [
    activeOrg?.name,
    constellations,
    dispatch,
    filters,
    navigate,
    parameters
  ]);

  return (
    <Flex
      className={styles.imageSearchParamsDiv}
      direction='column'
      align='start'
    >
      <Flex direction='row' align='start' gap='small' justify='between'>
        {loadingLocation ? (
          <Text>Loading location...</Text>
        ) : error ? (
          <Text>{error}</Text>
        ) : (
          <Text size={4} weight={500}>
            {locationName}
          </Text>
        )}
        <Button
          variant='outline'
          color='neutral'
          onClick={RedirectToExplore}
          data-test-id='viewsearchparams'
        >
          View
        </Button>
      </Flex>
      <Flex direction='column' align='center' gap='medium'>
        <For each={parameters}>
          {param => (
            <React.Fragment key={param.label}>
              {renderParam(param.label, param.value)}
            </React.Fragment>
          )}
        </For>
      </Flex>
    </Flex>
  );
};
